<template>
  <template
    v-if="streams.length"
  >
    <call-session-user-card
      v-for="(stream, i) in streams"
      :key="stream.getVideoTracks()[0]?.id ?? stream.getAudioTracks()[0]?.id ?? stream.id ?? `stream-${i}`"
      :class="$attrs['class']"
      :contact="contact"
      :selected-contact-id="selectedContactId!"
      :room-p-served="roomPServed"
      :session="session!"
      :can-scale-video="canScaleVideo!"
      :hide-border="hideBorder!"
      :is-big="isBig!"
      :light="light"
      :stream
      :plays-participant-audio="isPlaysParticipantAudioEnabled && getPlaysParticipantAudio(stream)"
      :selected-by-stream="getIsSelected(stream.id)"
      @select="onSelect($event, stream)"
    >
      <template #indicator>
        <slot
          name="indicator"
          :status-icon
        />
      </template>
      <template #action>
        <slot
          name="action"
          :member-status
        />
      </template>
      <template #avatar-footer>
        <slot
          name="avatar-footer"
          :contact-name="contact.name"
          :count="memberStatus === MemberStatus.Dialing ? participantsCount : null"
          :video-active="videoActive"
        />
      </template>
      <template #footer="{ contactName, title }">
        <slot
          name="footer"
          :contact-name
          :video-active
          :title
          :count="memberStatus === MemberStatus.Dialing ? participantsCount : null"
        />
      </template>
    </call-session-user-card>
  </template>
  <template
    v-else
  >
    <call-session-user-card
      :class="$attrs['class']"
      :contact
      :selected-contact-id="selectedContactId!"
      :room-p-served
      :session="session!"
      :hide-border="hideBorder!"
      :is-big="isBig!"
      :light="light"
      :plays-participant-audio="isPlaysParticipantAudioEnabled"
      @select="onSelect"
    >
      <template #indicator>
        <slot
          name="indicator"
          :status-icon
        />
      </template>
      <template #action>
        <slot
          name="action"
          :member-status
        />
      </template>
      <template #avatar-footer>
        <slot
          name="avatar-footer"
          :contact-name="contact.name"
          :count="memberStatus === MemberStatus.Dialing ? participantsCount : null"
          :video-active="videoActive"
        />
      </template>
      <template #footer="{ contactName, title }">
        <slot
          name="footer"
          :contact-name
          :video-active
          :title
          :count="memberStatus === MemberStatus.Dialing ? participantsCount : null"
        />
      </template>
    </call-session-user-card>
  </template>
</template>

<script setup lang="ts">
import { IconName } from '@wui/common-library'
import { PServed } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, toRefs, ref, watch } from 'vue'



import { useCallSessionMediaStore } from '@/entities/call-session-media'
import { useStoreConferenceMediaStatuses, ConferenceParticipant } from '@/entities/conference-participant'
import { useStoreRooms, useIntercomRoomParticipantsStatusesStore, MemberStatus } from '@/entities/room'
import { RTCSessionFacade, WEBCAM_STREAM_NAME, SCREEN_SHARING_STREAM_NAME } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { getTrackNameFromTrackId, getParticipantShortCallIdFromTrackId } from '@/shared/services/media-helper'

import { CallSessionUserCard } from '.'

const props = defineProps<{
  contact: ConferenceParticipant
  selectedContactId?: string
  selectedStreamId?: string
  roomPServed: PServed
  session?: RTCSessionFacade
  canScaleVideo?: boolean
  hideBorder?: boolean
  isBig?: boolean
  light?: boolean
  /** Filmstrip / thumbnail tiles should pass false to avoid double audio with Large. Default true. */
  playsParticipantAudio?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', contactId: string, stream?: MediaStream): void
}>()

const { session, contact, roomPServed, selectedContactId, selectedStreamId } = toRefs(props)

const isPlaysParticipantAudioEnabled = computed(() => props.playsParticipantAudio !== false)

const { getParticipantMediaStatus, getConferenceParticipants } = useStoreConferenceMediaStatuses()
const { currentUser } = useCurrentUser()
const { currentGuest } = useGuestAuth()
const { getParticipantStatus } = useIntercomRoomParticipantsStatusesStore()
const { streamsBySessionId } = storeToRefs(useCallSessionMediaStore())

const videoSelectorIsShowed = ref(false)

const memberStatus = computed(() => getParticipantStatus(roomPServed.value, contact.value.internalNumber, contact.value.callId))

const memberMediaState = computed(() => getParticipantMediaStatus(contact.value.callId))

const isCurrentUser = computed(() => {
  const isSubscriber = currentUser.value && contact.value.imLogin && currentUser.value.imLogin === contact.value.imLogin && session.value?.callId.value === contact.value.callId
  const isGuest = currentGuest.value && contact.value.imLogin && currentGuest.value.pServed === contact.value.imLogin
  return isSubscriber || isGuest
})

const isOnHold = computed(() => {
  return memberStatus.value === MemberStatus.OnHold
})

const videoActive = computed(() => {
  if (!memberMediaState.value || memberMediaState.value.isVideoMuted || memberStatus.value === 'Dialing') {
    return false
  }
  return true
})

const isScreenSharingActive = computed(() => {
  if (!memberMediaState.value || memberMediaState.value.isScreenSharingMuted || memberStatus.value === 'Dialing') {
    return false
  }
  return true
})

const statusIcon = computed<{ mic: IconName, hand?: IconName | null }>(() => {
  return {
    mic: isOnHold.value ? 'pause' : (!memberMediaState.value || memberMediaState.value?.isAudioMuted) ? 'micOffM' : 'micM' as IconName,
    hand: memberMediaState.value?.handEnabled ? 'frontHand' : null,
  }
})

const streams = computed(() => {
  const sessionId = session.value?.sessionId
  if (!sessionId) {
    return []
  }
  if (isCurrentUser.value) {
    const streamsArray: MediaStream[] = []
    if (session.value?.localVideoStream.value) {
      streamsArray.push(session.value.localVideoStream.value)
    }
    if (session.value?.localScreenStream.value) {
      streamsArray.push(session.value.localScreenStream.value)
    }
    return streamsArray
  } else {
    const streams = streamsBySessionId.value.get(sessionId)?.filter((stream) => {
      let result = false
      stream.getVideoTracks().forEach(track => {
        if (track.readyState === 'ended') {
          return
        }
        if (getParticipantShortCallIdFromTrackId(track.id) !== contact.value.shortCallId) {
          return false
        }

        const trackName = getTrackNameFromTrackId(track.id)
        if (trackName === WEBCAM_STREAM_NAME && !videoActive.value) {
          result = false
        } else if (trackName === SCREEN_SHARING_STREAM_NAME && !isScreenSharingActive.value) {
          result = false
        } else {
          result = true
        }
        return
      })
      return result
    })
    ?? []
    return streams
  }
})

watch(videoSelectorIsShowed, (v) => {
  if (v) {
    setTimeout(() => {
      videoSelectorIsShowed.value = false
    }, 5000)
  }
})

const isUserSelected = computed(() => {
  return selectedContactId.value === contact.value.callId
})

const getIsSelected = (streamId?: string) => {
  if (!streamId || streams.value.length === 1) {
    return isUserSelected.value
  }

  return isUserSelected.value && selectedStreamId.value === streamId
}

const getPlaysParticipantAudio = (mediaStream: MediaStream) => {
  if (session.value?.localScreenStream.value?.id === mediaStream.id) {
    return false
  }
  const track = mediaStream.getVideoTracks().at(0)
  if (!track) return true
  return getTrackNameFromTrackId(track.id) === WEBCAM_STREAM_NAME
}

const onSelect = (contactId: string, stream?: MediaStream) => {
  emit('select', contactId, stream)
}

const { findByPServed } = useStoreRooms()
const participantsCount = computed<number>(() => {
  const room = findByPServed(props.roomPServed)
  if (room) {
    return getConferenceParticipants(room).filter(p => p.imLogin === props.contact.imLogin).length
  }

  return 1
})

</script>
